import React from "react";
import { DataGrid, GridColDef } from "@mui/x-data-grid";
import {
  Card,
  CardHeader,
  Dialog,
  DialogTitle,
  DialogContent,
  TextField,
  Button,
  DialogActions,
} from "@mui/material";
import { LoadingButton } from "@mui/lab";
import { useCreateOrderMutation } from "features/trade/tradeApi";
import { useFormik } from "formik";
import * as Yup from "yup";
interface Item {
  quantity: number;
  price: number;
}

interface OrderBookProps {
  type: "buy" | "sell";
  title: string;
  items: Item[];
}

const OrderBook: React.FC<OrderBookProps> = ({ items, title, type }) => {
  const [createOrder] = useCreateOrderMutation();

  const [open, setOpen] = React.useState<boolean>(false);

  const columns: GridColDef[] = [
    { field: "quantity", headerName: "Quantity", type: "number", width: 150 },
    { field: "price", headerName: "Price", type: "number", width: 150 },
  ];

  const { handleChange, handleSubmit, values, errors, isValid, isSubmitting } =
    useFormik({
      initialValues: {
        price: 0,
        quantity: 0,
      },
      validationSchema: Yup.object({
        price: Yup.number()
          .required("Price is required")
          .positive("Price must be a positive number"),
        quantity: Yup.number()
          .required("Quantity is required")
          .positive("Quantity must be a positive number"),
      }),
      onSubmit: async (values, { setSubmitting, resetForm }) => {
        try {
          console.log("Submitting order:", values);
          const response = await createOrder({
            symbol: "BTC/USD",
            side: type.toUpperCase(),
            limit: values.price,
            quantity: values.quantity,
          }).unwrap();
          console.log("Order created successfully:", response);
          resetForm();
          setOpen(false);
        } catch (error) {
          console.error("Failed to create order:", error);
        } finally {
          setSubmitting(false);
        }
      },
    });
  return (
    <div>
      <Dialog open={open} onClose={() => setOpen(false)}>
        <DialogTitle>Create {type} order</DialogTitle>
        <form onSubmit={handleSubmit}>
          <DialogContent>
            <TextField
              autoFocus
              margin="dense"
              label="Price"
              type="number"
              fullWidth
              name="price"
              value={values.price}
              onChange={handleChange}
              error={!!errors.price}
              helperText={errors.price}
            />
            <TextField
              margin="dense"
              label="Quantity"
              type="number"
              fullWidth
              name="quantity"
              value={values.quantity}
              onChange={handleChange}
              error={!!errors.quantity}
              helperText={errors.quantity}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setOpen(false)} color="secondary">
              Cancel
            </Button>
            <Button
              type="submit"
              color="primary"
              disabled={!isValid || isSubmitting}
            >
              Create
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          margin: "16px",
          padding: "0 16px",
        }}
      >
        {type === "buy" ? (
          <LoadingButton
            color="success"
            variant="contained"
            onClick={() => {
              setOpen(true);
            }}
          >
            Buy
          </LoadingButton>
        ) : (
          <LoadingButton
            color="error"
            variant="contained"
            onClick={() => {
              setOpen(true);
            }}
          >
            Sell
          </LoadingButton>
        )}
      </div>

      <Card>
        <CardHeader title={title} />
        <DataGrid
          rows={items.map((item, index) => ({ id: index, ...item }))}
          hideFooterPagination={true}
          columns={columns}
          style={{ height: "calc(100% - 64px)" }}
        />
      </Card>
    </div>
  );
};

export default OrderBook;
